import { Card, AccordionCollapse, FormGroup, FormLabel, FormSelect, InputGroup, FormControl } from "react-bootstrap"
import { cleanNumberInput, errorStyles, getTwoDigitLocaleString, isSectionComplete } from "../Utilities/constants"
import { SectionIndicators } from "./Common"
import { PricingField, SectionProps } from "../Types/calculator"
import { incrementDecrementArrows } from "../Utilities/interface"
import React from "react"

export function Pricing(props: SectionProps<PricingField>) {
  const { onCategorySelected, state, section, setSettings } = props
  const { expandedSections, useCalculatedCostPerUnit, calculatedCostPerUnit, costPerUnit, annualFees } = state
  const complete = isSectionComplete(state, section)

  const validateCostPerUnit = section.fields["costPerUnit"].validationFunction
  const validateAnnualFees = section.fields["annualFees"].validationFunction

  const [customCostPerUnit, setCustomCostPerUnit] = React.useState<string>()
  const formatRef = React.useRef<number | undefined>()
  const updateCostPerUnit = (value: string) => {
    if (formatRef.current) {
      window.clearTimeout(formatRef.current)
    }
    formatRef.current = window.setTimeout(() => {
      formatRef.current = undefined;
      setSettings({ costPerUnit: cleanNumberInput(value) })
    }, 450)
    setCustomCostPerUnit(value)
  }

  return (
    <Card id="Pricing">
      <Card.Header
        tabIndex={20}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onCategorySelected("Pricing")}
        as="h3"
        onClick={() => onCategorySelected("Pricing")}
      >
        <div style={{ fontWeight: complete && expandedSections.includes("Pricing") ? "lighter" : "inherit" }}>{section.name}</div>
        <SectionIndicators
          error={!complete && expandedSections.includes("Pricing")}
          complete={complete}
          tooltipMessage="A price quote from a dealer or supplier can be input here. Default values based on some historical data reported by fleets can also be used."
        />
      </Card.Header>
      <AccordionCollapse className="IncludeInExport" eventKey="Pricing">
        <Card.Body>
          <FormGroup>
            <FormLabel aria-label="Cost per Vehicle">
              Would you like to use a calculated price per vehicle based on your technology selections, or enter your own price per vehicle?
              <FormSelect
                tabIndex={21}
                onChange={(ev) => ev?.target && setSettings({ useCalculatedCostPerUnit: "Yes" === ev.target.value })}
                value={typeof useCalculatedCostPerUnit === "boolean" ? (useCalculatedCostPerUnit ? "Yes" : "No") : undefined}
              >
                <option value="Yes">Calculated Price per Vehicle</option>
                <option value="No">Custom Price per Vehicle</option>
              </FormSelect>
            </FormLabel>
          </FormGroup>
          <FormGroup>
            <FormLabel aria-label="Cost per Vehicle">
              Cost per Vehicle
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                {useCalculatedCostPerUnit ? <FormControl disabled value={getTwoDigitLocaleString(calculatedCostPerUnit)} /> : (
                  <FormControl
                    tabIndex={22}
                    value={formatRef.current ? customCostPerUnit : getTwoDigitLocaleString(costPerUnit)}
                    min={0}
                    type="text"
                    onChange={(ev) => updateCostPerUnit(ev.target.value)}
                    onKeyDown={(ev) => incrementDecrementArrows({ ev, field: "costPerUnit", currency: true, state, setSettings, onlyUpDown: true })}
                    onBlur={(ev) => setSettings({ costPerUnit: cleanNumberInput(ev.target.value) })}
                    style={!validateCostPerUnit(costPerUnit) ? errorStyles : undefined}
                  />
                )}
              </InputGroup>
            </FormLabel>
          </FormGroup>
          <FormGroup>
            <FormLabel aria-label="Annual Fees per Vehicle">
              Enter the annual fees or subscription costs per vehicle associated with this technology (if any)
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <FormControl
                  tabIndex={23}
                  value={annualFees.toLocaleString()}
                  type="text"
                  min={0}
                  onChange={(ev) => setSettings({ annualFees: cleanNumberInput(ev.target.value) })}
                  onKeyDown={(ev) => incrementDecrementArrows({ ev, field: "annualFees", currency: true, state, setSettings, onlyUpDown: true })}
                  onBlur={(ev) => setSettings({ annualFees: cleanNumberInput(ev.target.value) })}
                  style={!validateAnnualFees(annualFees) ? errorStyles : undefined}
                />
              </InputGroup>
            </FormLabel>
          </FormGroup>
        </Card.Body>
      </AccordionCollapse>
    </Card>
  )
}